import { currencyFormatter } from '@/app/common/utilities'
import { Card, CardContent, CardHeader } from '../Card'
import Image from '../Image/Image'
import { useTranslation } from 'react-i18next'

type DocumentMeta = {
  path: string
  student_id?: number
  student_name?: string
  invoice_number?: string
  invoice_date?: string
  amount?: number
  notes?: string
}

type Props = {
  documents: DocumentMeta[]
  students?: { student_id: number; student_name: string }[]
  title?: string
}

export function DocumentsViewer({ documents, students }: Props) {
  const { t } = useTranslation()

  if (documents.length === 0) return null

  const hasMetadata = documents.some(
    (doc) => doc.invoice_number || doc.amount != null
  )

  if (!hasMetadata) {
    return (
      <Card>
        <CardHeader>
          <h2 className="text-lg font-bold">{t('documents')}</h2>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 flex-wrap">
            {documents.map((doc, index) => (
              <div key={index}>
                <Image
                  allowZoom={true}
                  className="object-cover h-32 w-32"
                  path={doc.path}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <h2 className="text-lg font-bold">{t('documents')}</h2>
      </CardHeader>
      <CardContent className="space-y-3">
        {documents.map((doc, index) => {
          const studentName =
            doc.student_name ??
            students?.find((s) => s.student_id === doc.student_id)?.student_name
          return (
            <div
              key={index}
              className="flex gap-3 border border-gray-200 rounded-lg p-3"
            >
              <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-50">
                <Image
                  allowZoom={true}
                  className="object-cover w-full h-full"
                  path={doc.path}
                />
              </div>
              <div className="flex-1 min-w-0 space-y-0.5 text-sm">
                {studentName && (
                  <p className="font-medium text-gray-800">
                    {studentName} (Student)
                  </p>
                )}
                {doc.invoice_number && (
                  <p className="text-gray-600">
                    {t('Invoice #')} {doc.invoice_number}
                  </p>
                )}
                {doc.invoice_date && (
                  <p className="text-gray-600">{doc.invoice_date}</p>
                )}
                {doc.amount != null && (
                  <p className="font-semibold text-gray-800">
                    {currencyFormatter.format(doc.amount)}
                  </p>
                )}
                {doc.notes && (
                  <p className="text-gray-500 italic truncate">{doc.notes}</p>
                )}
              </div>
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}
